import React, { useState, useEffect } from 'react';
import { Gift, TrendingUp, Users, Award, DollarSign, Calendar } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { format } from 'date-fns';

const ReferralRewardsPanel = ({ onViewDetails }) => {
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalRewards: 0,
    activeReferrers: 0,
    monthlyReferrals: 0,
    topReferrers: []
  });
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [milestoneAlerts, setMilestoneAlerts] = useState([]);

  useEffect(() => {
    loadReferralData();
    
    // Set up real-time subscription for new referrals
    const channel = supabase
      ?.channel('referral-rewards-changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals'
        },
        () => {
          loadReferralData();
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  const loadReferralData = async () => {
    setLoading(true);
    try {
      // Get all referrals with user profiles
      const { data: referrals, error: referralsError } = await supabase
        ?.from('referrals')
        ?.select(`
          *,
          referrer:user_profiles!referrals_referrer_id_fkey(full_name, email),
          referred:user_profiles!referrals_referred_user_id_fkey(full_name, email)
        `)
        ?.order('created_at', { ascending: false });

      if (referralsError) {
        logger?.error('Failed to fetch referrals', referralsError);
        return;
      }

      // Calculate statistics
      const convertedReferrals = referrals?.filter(r => r?.status === 'converted') || [];
      const totalRewards = convertedReferrals?.reduce((sum, r) => sum + parseFloat(r?.reward_amount || 0), 0);
      
      // Get unique referrers
      const uniqueReferrers = new Set(convertedReferrals?.map(r => r?.referrer_id));
      
      // Get monthly referrals (current month)
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthlyReferrals = convertedReferrals?.filter(r => 
        new Date(r?.converted_at) >= monthStart
      )?.length || 0;

      // Get top referrers
      const referrerStats = {};
      convertedReferrals?.forEach(r => {
        if (!referrerStats?.[r?.referrer_id]) {
          referrerStats[r?.referrer_id] = {
            referrerId: r?.referrer_id,
            name: r?.referrer?.full_name || 'Unknown',
            email: r?.referrer?.email || '',
            count: 0,
            totalRewards: 0
          };
        }
        referrerStats[r?.referrer_id].count += 1;
        referrerStats[r?.referrer_id].totalRewards += parseFloat(r?.reward_amount || 0);
      });

      const topReferrers = Object.values(referrerStats)
        ?.sort((a, b) => b?.count - a?.count)
        ?.slice(0, 5);

      setReferralStats({
        totalReferrals: convertedReferrals?.length || 0,
        totalRewards: totalRewards,
        activeReferrers: uniqueReferrers?.size || 0,
        monthlyReferrals: monthlyReferrals,
        topReferrers: topReferrers
      });

      // Get recent referrals (last 10)
      setRecentReferrals(referrals?.slice(0, 10) || []);

      // Get milestone alerts (users who reached 5 or 10 referrals)
      const { data: milestones, error: milestonesError } = await supabase
        ?.from('referral_milestones')
        ?.select(`
          *,
          user:user_profiles(full_name, email)
        `)
        ?.order('achieved_at', { ascending: false })
        ?.limit(5);

      if (!milestonesError) {
        setMilestoneAlerts(milestones || []);
      }
    } catch (error) {
      logger?.error('Failed to load referral data', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={32} className="text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Gift className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Referral Rewards Tracking</h2>
                <p className="text-purple-100 text-sm">Monitor all users' referral progress and rewards</p>
              </div>
            </div>
            {onViewDetails && (
              <button
                onClick={onViewDetails}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Icon name="TrendingUp" size={16} />
                View Analytics
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 border-b border-gray-200">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Referrals</span>
            <Users className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-blue-900">{referralStats?.totalReferrals}</p>
          <p className="text-xs text-blue-700 mt-1">{referralStats?.monthlyReferrals} this month</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Total Rewards</span>
            <DollarSign className="text-green-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-green-900">£{referralStats?.totalRewards?.toFixed(2)}</p>
          <p className="text-xs text-green-700 mt-1">Distributed to users</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-900">Active Referrers</span>
            <TrendingUp className="text-purple-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-purple-900">{referralStats?.activeReferrers}</p>
          <p className="text-xs text-purple-700 mt-1">Users with conversions</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-900">Avg per User</span>
            <Award className="text-orange-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-orange-900">
            {referralStats?.activeReferrers > 0 
              ? (referralStats?.totalReferrals / referralStats?.activeReferrers)?.toFixed(1)
              : '0.0'
            }
          </p>
          <p className="text-xs text-orange-700 mt-1">Referrals per referrer</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
        {/* Top Referrers */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="text-purple-600" size={20} />
            Top Referrers
          </h3>
          <div className="space-y-3">
            {referralStats?.topReferrers?.length > 0 ? (
              referralStats?.topReferrers?.map((referrer, index) => (
                <div key={referrer?.referrerId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-400 text-orange-900': 'bg-blue-100 text-blue-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{referrer?.name}</p>
                      <p className="text-xs text-gray-500">{referrer?.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{referrer?.count} referrals</p>
                    <p className="text-xs text-green-600">£{referrer?.totalRewards?.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No referrers yet</p>
            )}
          </div>
        </div>

        {/* Recent Milestone Achievements */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="Trophy" size={20} className="text-yellow-600" />
            Recent Milestones
          </h3>
          <div className="space-y-3">
            {milestoneAlerts?.length > 0 ? (
              milestoneAlerts?.map((milestone) => (
                <div key={milestone?.id} className="flex items-start gap-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="bg-yellow-400 p-2 rounded-full">
                    <Icon name="Trophy" size={16} className="text-yellow-900" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{milestone?.user?.full_name}</p>
                    <p className="text-sm text-gray-600">
                      Reached {milestone?.milestone_type} referrals!
                    </p>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Calendar size={12} />
                      {format(new Date(milestone?.achieved_at), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  {milestone?.email_sent && (
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                      Email Sent
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No milestones achieved yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Referrals Table */}
      <div className="p-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Referrals</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Referrer</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Referred User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Reward</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
              </tr>
            </thead>
            <tbody>
              {recentReferrals?.length > 0 ? (
                recentReferrals?.map((referral) => (
                  <tr key={referral?.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{referral?.referrer?.full_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{referral?.referrer?.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{referral?.referred?.full_name || 'Pending'}</p>
                        <p className="text-xs text-gray-500">{referral?.referred?.email || 'Not signed up yet'}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(referral?.status)}`}>
                        {referral?.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm font-semibold text-green-600">
                        {referral?.status === 'converted' ? `£${parseFloat(referral?.reward_amount || 0)?.toFixed(2)}` : '-'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {format(new Date(referral?.created_at), 'MMM dd, yyyy')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-sm text-gray-500">
                    No referrals yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReferralRewardsPanel;